import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TosModal from './modals/TosModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProtectedRoute({ children }) {
  const { user, loading, logout } = useAuth();
  const [tosChecked, setTosChecked] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [checkingTos, setCheckingTos] = useState(true);

  const checkTos = useCallback(async () => {
    if (!user || user === false) {
      setCheckingTos(false);
      return;
    }
    // Admins skip ToS — only sub-users and regular users see it
    if (user.role === 'admin' || user.role === 'SUPER_ADMIN') {
      setTosAccepted(true);
      setTosChecked(true);
      setCheckingTos(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/tos/status`);
      setTosAccepted(data.accepted === true);
      setTosChecked(true);
    } catch (err) {
      // Only skip ToS if it's a 401 (token invalid) - redirect to login
      if (err.response?.status === 401) {
        setTosAccepted(false);
        setTosChecked(false);
      } else {
        // For any other error (network, 500), still require ToS acceptance
        setTosAccepted(false);
        setTosChecked(true);
      }
    }
    setCheckingTos(false);
  }, [user]);

  // Reset ToS state whenever user changes (new login)
  useEffect(() => {
    setTosChecked(false);
    setTosAccepted(false);
    setCheckingTos(true);
    if (user && user !== false) {
      checkTos();
    } else {
      setCheckingTos(false);
    }
  }, [user, checkTos]);

  const handleAcceptTos = async () => {
    try {
      await axios.post(`${API_URL}/api/tos/accept`, { accepted: true });
      setTosAccepted(true);
    } catch (err) {
      console.error('ToS accept failed:', err);
    }
  };

  const handleDeclineTos = async () => {
    await logout();
  };

  if (loading || checkingTos) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,8%)]">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-[hsl(215,16%,56%)]">Loading...</p>
        </div>
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" replace />;
  }

  // Show ToS modal if not accepted — applies to ALL users (admin, sub-user, regular)
  if (tosChecked && !tosAccepted) {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,8%)]">
        <TosModal onAccept={handleAcceptTos} onDecline={handleDeclineTos} />
      </div>
    );
  }

  return children;
}
